import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { decrypt } from '../../../functions/encryption'
import { InvoiceMultiPageSetting } from '../../../interfaces/Invoices'
import { getInvoiceGeneratedByLatest } from '../../../api/InvoicesCRUD'
import { downloadInvoicePDF } from '../../../components/Invoices/InvoiceUtils'
import { useIntl } from 'react-intl'

const KEY_ENCRYPT = process.env.REACT_APP_KEY_ENCRYPT

const AutoDownloadInvoice = () => {
    const { token, progressId, leadId } = useParams()
    const [tokenDecrypt, setTokenDecrypt] = useState<string | null>(null)
    const [selectInvoice, setSelectInvoice] = useState<InvoiceMultiPageSetting | null>(null)
    const [isDownloading, setIsDownloading] = useState(false)
    const [downloadStatus, setDownloadStatus] = useState<string>('')
    const [hasDownloaded, setHasDownloaded] = useState(false)
    const intl = useIntl()

    useEffect(() => {
        if (token && KEY_ENCRYPT) {
            try {
                const decrypted = decrypt(token, KEY_ENCRYPT)
                setTokenDecrypt(decrypted)
            } catch (error) {
                console.error('Token decryption failed:', error)
                setDownloadStatus('Error: Invalid token')
            }
        } else {
            setDownloadStatus('Error: Missing token or encryption key')
        }
    }, [token])

    const getInvoiceGenerated = React.useCallback(async () => {
        try {
            setDownloadStatus('Fetching invoice data...')

            if (tokenDecrypt && progressId && leadId) {
                const response = await getInvoiceGeneratedByLatest(tokenDecrypt, progressId, leadId)

                if (response.data.data) {
                    setSelectInvoice(response.data.data)
                    setDownloadStatus('Invoice data loaded, preparing download...')
                } else {
                    setDownloadStatus('Error: No invoice data found')
                }
            } else {
                setDownloadStatus('Error: Missing required parameters')
            }
        } catch (error) {
            console.error('Invoice fetch error:', error)
            setSelectInvoice(null)
            setDownloadStatus(`Error: ${error instanceof Error ? error.message : 'Failed to fetch invoice data'}`)
        }
    }, [tokenDecrypt, progressId, leadId])

    const handleDownloadPDF = React.useCallback(async (invoice: InvoiceMultiPageSetting) => {
        if (hasDownloaded) {
            setDownloadStatus('File has already been downloaded. Please reload the page to download again.')
            return
        }

        setIsDownloading(true)
        setDownloadStatus('Preparing PDF download...')

        try {
            const result = await downloadInvoicePDF(invoice, intl)

            if (result.success) {
                setHasDownloaded(true)
                setDownloadStatus('PDF downloaded successfully!')
                setTimeout(() => {
                    setDownloadStatus('Download completed. You can close this window.')
                }, 2000)
            } else {
                setDownloadStatus(`Error: ${result.message}`)
                console.error('PDF download failed:', result.error)
            }
        } catch (error) {
            console.error('Download error:', error)
            setDownloadStatus(`Error: ${error instanceof Error ? error.message : 'Failed to download PDF'}`)
        } finally {
            setIsDownloading(false)
        }
    }, [intl, hasDownloaded])

    useEffect(() => {
        if (tokenDecrypt && progressId && leadId) {
            getInvoiceGenerated()
        }
    }, [tokenDecrypt, progressId, leadId, getInvoiceGenerated])

    useEffect(() => {
        if (selectInvoice) {
            handleDownloadPDF(selectInvoice)
        }
    }, [selectInvoice, handleDownloadPDF])

    return (
        <div className='bg-white w-100 h-100 d-flex align-items-center justify-content-center'>
            <div className='text-center'>
                {isDownloading ? (
                    <>
                        <div className='spinner-border text-primary mb-3' role='status'>
                            <span className='visually-hidden'>Loading...</span>
                        </div>
                        <p className='mb-0'>{downloadStatus}</p>
                    </>
                ) : (
                    <>
                        {hasDownloaded && (
                            <div className='mb-3'>
                                <i className='bi bi-check-circle-fill text-success' style={{ fontSize: '3rem' }}></i>
                            </div>
                        )}
                        <p className='mb-0'>{downloadStatus || 'Initializing...'}</p>
                        {hasDownloaded && (
                            <small className='text-muted mt-2 d-block'>
                                To download again, please reload this page.
                            </small>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}

export default AutoDownloadInvoice